import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import {
  Ban,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Maximize,
  Minus,
  Plus,
  RotateCcw,
  Undo2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { DecisionCheckbox } from "@/components/agents/configuration-server/stages/DecisionRows";
import { cn } from "@/lib/utils";

/**
 * Modo Mapa do Gerenciamento (Claude Design, `Management.dc.html`): a árvore
 * inteira da aba — pendentes e decididos — em cartões de 208×96 coloridos pelo
 * status, que se arrasta e amplia. Filtro apaga o que não bate, sem esconder.
 *
 * A estrutura organizacional passa de 4 mil unidades: o layout é calculado uma
 * vez por árvore e só os cartões dentro da área visível vão para o DOM.
 */

export type MapStatus = "pending" | "approved" | "out_of_scope";

export interface MapItem<T> {
  key: string;
  kind: "group" | "item";
  name: string;
  code?: string;
  type?: string;
  status?: MapStatus;
  /** Já executado (cadastrado no CBC / configurado): não volta para pendente. */
  done?: boolean;
  /** Nome do duplo check; sem ele, "Registered in CBC" (estrutura organizacional). */
  doneLabel?: string;
  checked?: boolean;
  /** A decisão desta unidade pertence ao Diagnóstico. */
  locked?: boolean;
  sourceLabel?: string;
  dimmed?: boolean;
  data: T;
  children: MapItem<T>[];
}

const NW = 208,
  NH = 96,
  GX = 20,
  GY = 48,
  RG = 40,
  PAD = 24;
const MIN_ZOOM = 0.3,
  MAX_ZOOM = 1.6;
const START = { zoom: 0.9, pan: { x: 24, y: 64 } };
/** jsdom e o primeiro render não têm tamanho: o design usa 1000×640 como referência. */
const FALLBACK = { w: 1000, h: 640 };

const STATUS: Record<MapStatus, { label: string; text: string; card: string }> =
  {
    pending: {
      label: "Pending",
      text: "text-[#8a5a00]",
      card: "border-[rgba(255,200,61,0.85)] bg-[#fffcf2] shadow-[0_0_0_3px_rgba(255,200,61,0.14),0_1px_2px_rgba(8,37,76,0.05)]",
    },
    approved: {
      label: "Approved",
      text: "text-[#1e7d42]",
      card: "border-[rgba(39,161,85,0.55)] bg-[#f6fbf8] shadow-[0_0_0_3px_rgba(39,161,85,0.10),0_1px_2px_rgba(8,37,76,0.05)]",
    },
    out_of_scope: {
      label: "Out of scope",
      text: "text-[#a3262c]",
      card: "border-[rgba(212,53,60,0.45)] bg-[#fdf7f7] shadow-[0_0_0_3px_rgba(212,53,60,0.08),0_1px_2px_rgba(8,37,76,0.05)]",
    },
  };

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--exed-ink-900)]";
const CONTROL = cn(
  "flex h-8 w-8 items-center justify-center rounded text-[var(--exed-ink-900)] hover:bg-[#f1f3f6] [&_svg]:h-4 [&_svg]:w-4",
  FOCUS,
);

interface Placed<T> {
  occurrenceKey: string;
  item: MapItem<T>;
  x: number;
  y: number;
  open: boolean;
}

interface Edge {
  l: number;
  t: number;
  w: number;
  h: number;
}

function layout<T>(roots: MapItem<T>[], collapsed: ReadonlySet<string>) {
  const nodes: Placed<T>[] = [];
  const edges: Edge[] = [];
  let cursor = PAD;
  let maxDepth = 0;
  const place = (
    item: MapItem<T>,
    depth: number,
    path: string[],
  ): Placed<T> => {
    maxDepth = Math.max(maxDepth, depth);
    const open = item.children.length > 0 && !collapsed.has(item.key);
    const node: Placed<T> = {
      occurrenceKey: JSON.stringify([...path, item.key]),
      item,
      x: 0,
      y: PAD + depth * (NH + GY),
      open,
    };
    nodes.push(node);
    if (open) {
      const placed = item.children.map((child) =>
        place(child, depth + 1, [...path, item.key]),
      );
      node.x = (placed[0].x + placed[placed.length - 1].x) / 2;
      const top = node.y + NH,
        middle = top + GY / 2;
      edges.push({ l: node.x + NW / 2 - 1, t: top, w: 2, h: GY / 2 });
      const xs = placed.map((child) => child.x + NW / 2);
      const min = Math.min(...xs),
        max = Math.max(...xs);
      if (max > min)
        edges.push({ l: min - 1, t: middle - 1, w: max - min + 2, h: 2 });
      for (const x of xs) edges.push({ l: x - 1, t: middle, w: 2, h: GY / 2 });
    } else {
      node.x = cursor;
      cursor += NW + GX;
    }
    return node;
  };
  for (const root of roots) {
    place(root, 0, []);
    cursor += RG - GX;
  }
  return {
    nodes,
    edges,
    w: cursor - RG + PAD,
    h: PAD * 2 + (maxDepth + 1) * NH + maxDepth * GY,
  };
}

function countByStatus<T>(roots: MapItem<T>[]) {
  const counts: Record<MapStatus, number> = {
    pending: 0,
    approved: 0,
    out_of_scope: 0,
  };
  const walk = (items: MapItem<T>[]) => {
    for (const item of items) {
      if (item.kind === "item" && item.status) counts[item.status] += 1;
      walk(item.children);
    }
  };
  walk(roots);
  return counts;
}

export function DecisionMap<T>({
  roots,
  onCheck,
  onApprove,
  onOut,
  onUndo,
}: {
  roots: MapItem<T>[];
  onCheck?: (data: T, checked: boolean) => void;
  onApprove?: (data: T) => void;
  onOut?: (data: T) => void;
  onUndo?: (data: T) => void;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [view, setView] = useState(START);
  const [size, setSize] = useState(FALLBACK);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(
    null,
  );
  const canvas = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = canvas.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth > 0)
        setSize({ w: element.clientWidth, h: element.clientHeight });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const map = useMemo(() => layout(roots, collapsed), [roots, collapsed]);
  const counts = useMemo(() => countByStatus(roots), [roots]);

  // Só o que cai na janela (com uma margem de um cartão) vai para o DOM.
  const left = -view.pan.x / view.zoom - NW,
    top = -view.pan.y / view.zoom - NH;
  const right = left + size.w / view.zoom + 2 * NW,
    bottom = top + size.h / view.zoom + 2 * NH;
  const visibleNodes = map.nodes.filter(
    (node) =>
      node.x + NW >= left &&
      node.x <= right &&
      node.y + NH >= top &&
      node.y <= bottom,
  );
  const visibleEdges = map.edges.filter(
    (edge) =>
      edge.l + edge.w >= left &&
      edge.l <= right &&
      edge.t + edge.h >= top &&
      edge.t <= bottom,
  );

  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const zoomTo = (target: number) =>
    setView((current) => {
      const zoom = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, Math.round(target * 100) / 100),
      );
      const ratio = zoom / current.zoom,
        cx = size.w / 2,
        cy = size.h / 2;
      return {
        zoom,
        pan: {
          x: cx - (cx - current.pan.x) * ratio,
          y: cy - (cy - current.pan.y) * ratio,
        },
      };
    });
  const fit = () => {
    const zoom = Math.min(
      1,
      Math.max(
        MIN_ZOOM,
        Math.min((size.w - 32) / map.w, (size.h - 110) / map.h),
      ),
    );
    const rounded = Math.round(zoom * 100) / 100;
    setView({
      zoom: rounded,
      pan: { x: Math.max(16, (size.w - map.w * rounded) / 2), y: 60 },
    });
  };

  const onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (
      event.button !== 0 ||
      (event.target as HTMLElement).closest("button, [role=checkbox]")
    )
      return;
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      px: view.pan.x,
      py: view.pan.y,
    };
    setDragging(true);
  };
  const onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (!start) return;
    setView((current) => ({
      ...current,
      pan: {
        x: start.px + event.clientX - start.x,
        y: start.py + event.clientY - start.y,
      },
    }));
  };
  const endDrag = () => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
  };

  return (
    <div
      ref={canvas}
      data-testid="map-canvas"
      role="region"
      aria-label={t("Map")}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      className={cn(
        "relative h-[640px] select-none overflow-hidden rounded-b-lg bg-[#fafbfc]",
        dragging ? "cursor-grabbing" : "cursor-grab",
      )}
    >
      <div
        data-testid="map-stage"
        className="absolute left-0 top-0 origin-top-left"
        style={{
          transform: `translate(${Math.round(view.pan.x)}px, ${Math.round(view.pan.y)}px) scale(${view.zoom})`,
          width: map.w,
          height: map.h,
        }}
      >
        {visibleEdges.map((edge) => (
          <div
            key={`${edge.l}:${edge.t}:${edge.w}:${edge.h}`}
            aria-hidden="true"
            className="absolute rounded-[1px] bg-[#c9d1dc]"
            style={{ left: edge.l, top: edge.t, width: edge.w, height: edge.h }}
          />
        ))}
        {visibleNodes.map((node) => (
          <MapCard
            key={node.occurrenceKey}
            node={node}
            onToggle={toggle}
            onCheck={onCheck}
            onApprove={onApprove}
            onOut={onOut}
            onUndo={onUndo}
          />
        ))}
      </div>

      <ul
        aria-label={t("Legend")}
        className="absolute left-3 top-3 flex items-center gap-3.5 rounded-md border border-[var(--exed-card-border)] bg-white px-3 py-2 shadow-[0_1px_2px_rgba(8,37,76,0.06)]"
      >
        {(Object.keys(STATUS) as MapStatus[]).map((status) => (
          <li
            key={status}
            className="flex items-center gap-1.5 text-xs text-[var(--exed-ink-500)]"
          >
            <span
              aria-hidden="true"
              className={cn(
                "h-3 w-3 rounded-[3px] border",
                STATUS[status].card,
                "shadow-none",
              )}
            />
            {t(STATUS[status].label)}
            <strong className="font-semibold text-[var(--exed-ink-900)]">
              {counts[status].toLocaleString("en-US")}
            </strong>
          </li>
        ))}
      </ul>

      <span className="absolute bottom-[18px] left-3.5 text-xs text-[var(--exed-ink-500)]">
        {t("Drag to move · Use the controls to zoom")}
      </span>

      <div className="absolute bottom-3 right-3 flex items-center gap-0.5 rounded-md border border-[var(--exed-card-border)] bg-white p-[3px] shadow-[0_1px_2px_rgba(8,37,76,0.06)]">
        <button
          type="button"
          aria-label={t("Zoom out")}
          title={t("Zoom out")}
          onClick={() => zoomTo(view.zoom - 0.1)}
          className={CONTROL}
        >
          <Minus aria-hidden="true" />
        </button>
        <span
          aria-live="polite"
          className="min-w-[44px] text-center text-xs font-semibold tabular-nums text-[var(--exed-ink-900)]"
        >
          {Math.round(view.zoom * 100)}%
        </span>
        <button
          type="button"
          aria-label={t("Zoom in")}
          title={t("Zoom in")}
          onClick={() => zoomTo(view.zoom + 0.1)}
          className={CONTROL}
        >
          <Plus aria-hidden="true" />
        </button>
        <span
          aria-hidden="true"
          className="mx-0.5 h-5 w-px bg-[var(--exed-card-border)]"
        />
        <button
          type="button"
          aria-label={t("Fit to screen")}
          title={t("Fit to screen")}
          onClick={fit}
          className={CONTROL}
        >
          <Maximize aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={t("Reset to 100%")}
          title={t("Reset to 100%")}
          onClick={() => setView({ zoom: 1, pan: START.pan })}
          className={CONTROL}
        >
          <RotateCcw aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function MapCard<T>({
  node,
  onToggle,
  onCheck,
  onApprove,
  onOut,
  onUndo,
}: {
  node: Placed<T>;
  onToggle: (key: string) => void;
  onCheck?: (data: T, checked: boolean) => void;
  onApprove?: (data: T) => void;
  onOut?: (data: T) => void;
  onUndo?: (data: T) => void;
}) {
  const { t } = useTranslation();
  const { item, x, y, open } = node;
  const id = item.code ?? item.name;
  const kids = item.children.length;
  const toggle = kids > 0 && (
    <button
      type="button"
      aria-expanded={open}
      aria-label={t(open ? "Collapse {{id}}" : "Expand {{id}}", { id })}
      onClick={() => onToggle(item.key)}
      className={cn(
        "ml-auto flex h-[26px] items-center gap-0.5 rounded-[5px] pl-2 pr-1.5 text-[11px] font-semibold",
        FOCUS,
        item.kind === "group"
          ? "bg-white/15 text-white hover:bg-white/25"
          : "bg-[rgba(8,37,76,0.06)] text-[var(--exed-ink-900)] hover:bg-[rgba(8,37,76,0.12)]",
      )}
    >
      {kids.toLocaleString("en-US")}
      {open ? (
        <ChevronDown aria-hidden="true" className="h-3 w-3" />
      ) : (
        <ChevronRight aria-hidden="true" className="h-3 w-3" />
      )}
    </button>
  );
  const position = { left: x, top: y, width: NW, height: NH };

  if (item.kind === "group") {
    return (
      <div
        data-testid={`map-node-${item.key}`}
        style={position}
        className={cn(
          "absolute flex flex-col gap-1.5 rounded-lg border border-[var(--exed-navy)] bg-[var(--exed-navy)] px-3 py-2.5 shadow-[0_1px_2px_rgba(8,37,76,0.15)]",
          item.dimmed && "opacity-35",
        )}
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-white/70">
          {t("Application area")}
        </span>
        <span className="truncate text-[13px] font-semibold text-white">
          {item.name}
        </span>
        <div className="mt-auto flex items-center">
          <span className="text-[11px] text-white/80">
            {t("{{count}} items", { count: kids })}
          </span>
          {toggle}
        </div>
      </div>
    );
  }

  const status = STATUS[item.status ?? "pending"];
  const pending = (item.status ?? "pending") === "pending";
  return (
    <div
      data-testid={`map-node-${item.key}`}
      style={position}
      className={cn(
        "absolute flex flex-col gap-1.5 rounded-lg border px-3 py-2.5",
        status.card,
        item.checked && "border-[var(--exed-navy)]",
        item.dimmed && "opacity-35",
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {pending && !item.locked && onCheck && (
          <DecisionCheckbox
            checked={item.checked ?? false}
            onChange={(value) => onCheck(item.data, value)}
            label={t("Select {{name}}", { name: id })}
          />
        )}
        <span className="min-w-0 flex-1 truncate font-mono text-[9px] uppercase tracking-[0.08em] text-[var(--exed-ink-500)]">
          {item.type}
        </span>
        <span className={cn("shrink-0 text-[10px] font-semibold", status.text)}>
          {t(status.label)}
        </span>
      </div>
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span className="shrink-0 font-mono text-[11px] font-medium text-[var(--exed-ink-900)]">
          {item.code}
        </span>
        <span
          title={item.name}
          className="truncate text-[13px] font-semibold text-[var(--exed-ink-900)]"
        >
          {item.name}
        </span>
      </div>
      <div className="mt-auto flex items-center gap-1">
        {item.sourceLabel && (
          <span className="shrink-0 rounded-full bg-[rgba(8,37,76,0.08)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--exed-ink-900)]">
            {item.sourceLabel}
          </span>
        )}
        {pending && !item.locked && onApprove && (
          <button
            type="button"
            aria-label={t("Approve {{code}}", { code: id })}
            title={t("Approve")}
            onClick={() => onApprove(item.data)}
            className={cn(
              "flex h-[26px] w-[26px] items-center justify-center rounded-[5px] border border-[rgba(39,161,85,0.3)] bg-white text-[#1e7d42] hover:border-[var(--exed-success)] hover:bg-[var(--exed-success)] hover:text-white",
              FOCUS,
            )}
          >
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        )}
        {pending && !item.locked && onOut && (
          <button
            type="button"
            aria-label={t("Take {{code}} out of scope", { code: id })}
            title={t("Take out of scope")}
            onClick={() => onOut(item.data)}
            className={cn(
              "flex h-[26px] w-[26px] items-center justify-center rounded-[5px] border border-[rgba(212,53,60,0.25)] bg-white text-[#a3262c] hover:border-[var(--exed-danger)] hover:bg-[var(--exed-danger)] hover:text-white",
              FOCUS,
            )}
          >
            <Ban aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        )}
        {!pending && item.done && (
          <span
            role="img"
            aria-label={item.doneLabel ?? t("Registered in CBC")}
            title={item.doneLabel ?? t("Registered in CBC")}
            className="flex h-[26px] items-center text-[#1e7d42]"
          >
            <CheckCheck aria-hidden="true" className="h-4 w-4" />
          </span>
        )}
        {!pending && !item.done && !item.locked && onUndo && (
          <button
            type="button"
            aria-label={t("Return {{code}} to pending", { code: id })}
            title={t("Return to pending")}
            onClick={() => onUndo(item.data)}
            className={cn(
              "flex h-[26px] items-center gap-1 rounded-[5px] border border-[var(--exed-card-border)] bg-white px-2 text-[11px] font-medium text-[var(--exed-ink-500)] hover:text-[var(--exed-ink-900)]",
              FOCUS,
            )}
          >
            <Undo2 aria-hidden="true" className="h-3 w-3" />
            {t("Undo")}
          </button>
        )}
        {toggle}
      </div>
    </div>
  );
}
